import { useState, useEffect } from "react"
import { Star, ChevronLeft, ChevronRight } from "lucide-react"
import { TESTIMONIALS_LIST } from "../../content/testimonials.list"
import SectionWrapper from "../ui/SectionWrapper"

/**
 * TestimonialCarousel - Desktop-only carousel with original beautiful design
 *
 * ARCHITECTURE (April 2026):
 * - Desktop-only: Hidden on mobile (<768px)
 * - Mobile uses TestimonialsSection (grid layout) instead
 * - See: TestimonialView wrapper component for breakpoint switching
 *
 * ORIGINAL DESIGN (Restored):
 * - Arrows positioned OUTSIDE the card (left: -70px, right: -70px)
 * - Arrows only visible on xl screens (1280px+)
 * - Clean arrow buttons without background styling
 * - Navigation dots: gap-2, mt-4 (tighter, closer to card)
 * - Card padding: p-[34px]
 * - No horizontal padding on carousel container
 *
 * FEATURES:
 * - Auto-advancing every 5 seconds + 2s (7s total)
 * - Progress bar inside active dot
 * - Manual navigation (arrows, dots)
 * - Pauses on hover
 *
 * @param {Object} props
 * @param {Array} props.clients - Array of client objects
 * @param {string} props.label - Section label
 * @param {string} props.subtitle - Section subtitle/h2
 */
export const TestimonialCarousel = ({ clients, label, subtitle }) => {
  const carouselClients = clients || TESTIMONIALS_LIST

  const [currentIndex, setCurrentIndex] = useState(0)
  const [progress, setProgress] = useState(0)
  const [isPaused, setIsPaused] = useState(false)

  // Auto-advance every 5 seconds + 2s = 7 seconds total
  // Progress updates every 100ms
  useEffect(() => {
    if (!isPaused) {
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            return 0
          }
          return prev + (100 / 70) // 7000ms total, updated every 100ms
        })
      }, 100)

      return () => clearInterval(progressInterval)
    }
  }, [isPaused])

  // When progress completes, move to next slide
  useEffect(() => {
    if (progress >= 100 && carouselClients.length > 0) {
      setCurrentIndex((prev) => (prev + 1) % carouselClients.length)
      setProgress(0)
    }
  }, [progress, carouselClients.length])

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % carouselClients.length)
    setProgress(0)
  }

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + carouselClients.length) % carouselClients.length)
    setProgress(0)
  }

  const handleDotClick = (index) => {
    if (index === currentIndex) return
    setCurrentIndex(index)
    setProgress(0)
  }

  if (!carouselClients || carouselClients.length === 0) {
    return null
  }

  // Direct R2 URL (no transformation - images served as-is)
  const getOptimizedImageUrl = (url) => {
    if (!url) return ''
    if (url.startsWith('http')) return url
    return `https://media.thelightcall.com${url}`
  }

  return (
    <SectionWrapper id="wall-of-love" data-testid="testimonial-carousel" className="!py-12 md:!py-16">
      {/* Section Title - centered */}
      <div className="content-spacing-lg text-center">
        <p className="typography-label">
          {label}
        </p>
        <h2 className="typography-h2-mobile md:typography-h2 text-center label-heading-spacing">
          {subtitle}
        </h2>
      </div>

      {/* Main Carousel Container */}
      <div
        className="relative"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Left Arrow - OUTSIDE card, xl screens only */}
        <button
          onClick={handlePrev}
          className="absolute left-[-70px] top-1/2 z-10 hidden -translate-y-1/2 text-color-secondary transition-opacity hover:opacity-70 xl:block"
          aria-label="Previous testimonial">
          <ChevronLeft className="w-icon h-icon" />
        </button>

        {/* Card Wrapper with border/background */}
        <div className="overflow-x-hidden rounded-2xl bg-color-card-overlay border border-color-light shadow-sm backdrop-blur-sm">
          {/* Sliding Cards Container */}
          <div
            className="flex transition-transform duration-[600ms] ease-in-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {carouselClients.map((t, idx) => (
              <div key={`${t.name}-${idx}`} className="min-w-full p-[34px]">
                {/* Top Row: Avatar + Name/Role + Stars */}
                <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-4">
                  <div className="flex items-center gap-4">
                    {t.image && (
                      <div className="h-[60px] w-[60px] flex-shrink-0 overflow-hidden rounded-full border border-color-primary shadow-md">
                        <img
                          src={getOptimizedImageUrl(t.image)}
                          width="60"
                          height="60"
                          alt={`${t.name}`}
                          loading="lazy"
                          className="h-full w-full scale-[1.03] object-cover object-center"
                        />
                      </div>
                    )}
                    <div>
                      <p className="font-primary text-xl font-semibold text-color-primary">{t.name}</p>
                      <p className="typography-label">
                        {t.context}
                      </p>
                    </div>
                  </div>
                  {/* 5 Stars - semantic star color (yellow) */}
                  <div className="flex items-center gap-0.5 text-color-star mr-8">
                    {Array.from({ length: 5 }).map((_, starIdx) => (
                      <Star key={starIdx} className="w-[19px] h-[19px] fill-current" />
                    ))}
                  </div>
                </div>

                {/* Quote - below stars */}
                <div className="text-color-text mt-3">
                  <p className="typography-body-narrative italic text-center sm:text-left">
                    "{t.quote}"
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Arrow - OUTSIDE card, xl screens only */}
        <button
          onClick={handleNext}
          className="absolute right-[-70px] top-1/2 z-10 hidden -translate-y-1/2 text-color-secondary transition-opacity hover:opacity-70 xl:block"
          aria-label="Next testimonial">
          <ChevronRight className="w-icon h-icon" />
        </button>
      </div>

      {/* Navigation Dots with Progress Bar inside active dot */}
      <div className="mt-4 flex justify-center gap-2">
        {carouselClients.map((_, index) => (
          <button
            key={index}
            onClick={() => handleDotClick(index)}
            className={`relative h-3 overflow-hidden rounded-full transition-all duration-300 ${
              index === currentIndex ? 'w-12 bg-color-primary' : 'w-3 bg-color-secondary'
            }`}
            aria-label={`Go to testimonial ${index + 1}`}>
            {/* Progress bar inside active dot */}
            {index === currentIndex && (
              <div
                className="absolute left-0 top-0 h-full bg-divider-on-dark transition-all duration-[100ms] ease-linear"
                style={{ width: `${progress}%` }}
              />
            )}
          </button>
        ))}
      </div>
    </SectionWrapper>
  )
}

export default TestimonialCarousel
